import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GetGeneralSettingResponse } from '@/modules/client-setting/type'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useHasPermission from '@/common/hooks/useHasPermission'

const formSchema = z.object({
	lineUrl: z.string().optional(),
	telegramUrl: z.string().optional(),
	clientDomain: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

interface ContactBankSettingBoxProps {
	data: GetGeneralSettingResponse
}

export default function ContactBankSettingBox({
	data,
}: ContactBankSettingBoxProps) {
	const { mutate: updateGeneralSetting } = useMutationUpdateGeneralSetting()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			lineUrl: data?.lineUrl ?? '',
			telegramUrl: data?.telegramUrl ?? '',
			clientDomain: data?.clientDomain ?? '',
		},
	})

	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const handleSubmit = (values: FormValues) => {
		try {
			updateGeneralSetting(
				{ payload: values },
				{
					onSuccess: () => {
						toast.success('บันทึกการตั้งค่าเรียบร้อยแล้ว')
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<div className="space-y-4 sm:space-y-6">
			<Card>
				<CardHeader className="pb-4 sm:pb-6">
					<CardTitle className="text-lg sm:text-xl">
						ตั้งค่าช่องทางติดต่อ
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4 sm:space-y-6">
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-4 sm:space-y-6"
						>
							<FormField
								control={form.control}
								name="lineUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											Line URL
										</FormLabel>
										<FormControl>
											<Input
												placeholder="กรอก Line URL"
												{...field}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="telegramUrl"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											Telegram URL
										</FormLabel>
										<FormControl>
											<Input
												placeholder="กรอก Telegram URL"
												{...field}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="clientDomain"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											โดเมนของลูกค้า (สำหรับลิงก์ช่องทาง)
										</FormLabel>
										<FormControl>
											<Input
												placeholder="กรอกโดเมนของลูกค้า"
												{...field}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{hasWritePermission && (
								<div className="flex justify-center pt-2 sm:justify-end sm:pt-4">
									<Button type="submit" size="lg" className="w-full sm:w-auto">
										บันทึก
									</Button>
								</div>
							)}
						</form>
					</Form>
				</CardContent>
			</Card>
		</div>
	)
}
